import React, { useState, useEffect, useCallback, useRef } from 'react';
import PropTypes from 'prop-types';
import {
  FaPlayCircle,
  FaShieldAlt,
  FaSyncAlt,
  FaTimes
} from 'react-icons/fa';
import { getReplaySession, getReplayRecordings } from '../../../../services/heatmap.service';
import { formatBytes, formatDateTime } from '../../shared/heatmapViewUtils';
import SessionReplayPlayer from '../replay/SessionReplayPlayer';

const getStatusBadgeClass = (statusVal) => {
  switch (statusVal?.toLowerCase()) {
    case 'ready':
    case 'complete':
      return 'bg-emerald-50 text-emerald-700 border-emerald-200';
    case 'held':
      return 'bg-amber-50 text-amber-700 border-amber-200';
    case 'incomplete':
      return 'bg-red-50 text-red-600 border-red-200';
    case 'expired':
    case 'sampled_out':
    default:
      return 'bg-slate-100 text-slate-600 border-slate-200';
  }
};

/**
 * Tab: Session Replay Inspection & Playback.
 * Allows querying, discovering, and playing back sanitized recordings using single-blob Read SAS URLs.
 * (API #14, API #15)
 */
export default function SessionReplayTab({
  organizationId,
  siteCollectionId,
  fromDate,
  toDate,
  initialSessionId = '',
  hasSessionReplayFeature = true,
  refreshNonce = 0,
  onLoadingChange
}) {
  const [activeSessionId, setActiveSessionId] = useState(initialSessionId);
  const [sessionData, setSessionData] = useState(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);

  // Recent recordings discovery list
  const [recordings, setRecordings] = useState([]);
  const [isLoadingRecordings, setIsLoadingRecordings] = useState(false);
  const [recordingsError, setRecordingsError] = useState(null);

  const fetchSession = useCallback(async (sId) => {
    if (!sId) {
      setSessionData(null);
      setError(null);
      return;
    }

    if (!organizationId || !siteCollectionId) {
      setError('Please select an organization and site collection to load session replays.');
      setSessionData(null);
      return;
    }

    setIsLoading(true);
    setError(null);

    try {
      const data = await getReplaySession(organizationId, siteCollectionId, sId);
      setSessionData(data);
    } catch (err) {
      setError(err?.message || 'Failed to retrieve session replay recording metadata');
      setSessionData(null);
    } finally {
      setIsLoading(false);
    }
  }, [organizationId, siteCollectionId]);

  const fetchRecordings = useCallback(async () => {
    if (!organizationId || !siteCollectionId || !hasSessionReplayFeature) {
      setRecordings([]);
      return;
    }

    setIsLoadingRecordings(true);
    setRecordingsError(null);

    try {
      const res = await getReplayRecordings(organizationId, siteCollectionId, { take: 20 });
      const list = Array.isArray(res)
        ? res
        : Array.isArray(res?.recordings)
          ? res.recordings
          : Array.isArray(res?.items)
            ? res.items
            : [];
      setRecordings(list);
    } catch (err) {
      setRecordings([]);
      setRecordingsError(err?.message || 'Failed to retrieve recent recordings');
    } finally {
      setIsLoadingRecordings(false);
    }
  }, [organizationId, siteCollectionId, hasSessionReplayFeature]);

  useEffect(() => {
    if (initialSessionId) {
      setActiveSessionId(initialSessionId);
    }
  }, [initialSessionId]);

  useEffect(() => {
    if (activeSessionId) {
      fetchSession(activeSessionId);
    }
  }, [activeSessionId, fetchSession]);

  useEffect(() => {
    fetchRecordings();
  }, [fetchRecordings]);

  // Report combined loading state to parent
  const isAnyLoading = isLoading || isLoadingRecordings;
  useEffect(() => {
    onLoadingChange?.(isAnyLoading);
  }, [isAnyLoading, onLoadingChange]);

  const activeSessionIdRef = useRef(activeSessionId);
  activeSessionIdRef.current = activeSessionId;
  const fetchSessionRef = useRef(fetchSession);
  fetchSessionRef.current = fetchSession;
  const fetchRecordingsRef = useRef(fetchRecordings);
  fetchRecordingsRef.current = fetchRecordings;

  // React to global toolbar refresh triggers
  useEffect(() => {
    if (refreshNonce > 0) {
      fetchRecordingsRef.current();
      if (activeSessionIdRef.current) {
        fetchSessionRef.current(activeSessionIdRef.current);
      }
    }
  }, [refreshNonce]);

  const handleSelectRecording = (sId) => {
    setActiveSessionId(sId);
  };

  const handleCloseModal = useCallback(() => {
    setActiveSessionId('');
    setSessionData(null);
    setError(null);
  }, []);

  // Close modal on Escape key press
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Escape' && activeSessionId) {
        handleCloseModal();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [activeSessionId, handleCloseModal]);

  if (!hasSessionReplayFeature) {
    return (
      <div className="flex flex-col gap-5 w-full" data-testid="session-replay-tab">
        <div
          className="flex flex-col items-center justify-center py-16 px-8 bg-white border border-slate-200 rounded-xl text-center gap-4 max-w-[600px] my-8 mx-auto"
          data-testid="replay-feature-denied"
        >
          <FaShieldAlt className="text-5xl text-amber-600" />
          <h3 className="text-xl font-extrabold text-slate-900 m-0">Session Replay Feature Required</h3>
          <p className="text-sm text-slate-500 leading-relaxed m-0">
            Your organization does not have an active subscription for Session Replay.
            Session Replay requires an enabled Session Replay entitlement, Customer Analytics, Heatmaps, and DAP-managed or BYO blob storage.
          </p>
        </div>
      </div>
    );
  }

  const isModalOpen = Boolean(activeSessionId);

  return (
    <div className="flex flex-col gap-5 w-full" data-testid="session-replay-tab">
      {/* Session Player Modal Overlay with Blurred Backdrop */}
      {isModalOpen && (
        <div
          className="fixed inset-0 z-50 flex items-center justify-center p-4 sm:p-6 lg:p-8 bg-slate-900/60 backdrop-blur-sm transition-all"
          onClick={handleCloseModal}
          data-testid="session-replay-modal-backdrop"
          role="dialog"
          aria-modal="true"
          aria-labelledby="session-replay-modal-title"
        >
          <div
            className="relative w-full max-w-6xl max-h-[92vh] bg-white rounded-2xl shadow-2xl overflow-hidden flex flex-col border border-slate-200/80"
            onClick={(e) => e.stopPropagation()}
            data-testid="session-replay-modal"
          >
            {/* Modal Header */}
            <div className="flex items-center justify-between px-5 py-3.5 bg-slate-50 border-b border-slate-200">
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center w-7 h-7 rounded-lg bg-blue-100 text-blue-600">
                  <FaPlayCircle className="text-sm" />
                </span>
                <div>
                  <h3 id="session-replay-modal-title" className="text-sm font-bold text-slate-900 m-0 leading-tight">
                    Session Replay Playback
                  </h3>
                  <span className="text-[11px] text-slate-500 font-mono">
                    Session: {activeSessionId}
                  </span>
                </div>
              </div>
              <button
                type="button"
                onClick={handleCloseModal}
                className="inline-flex items-center justify-center w-8 h-8 rounded-lg text-slate-400 hover:text-slate-700 hover:bg-slate-200/70 transition-colors focus:outline-none focus:ring-2 focus:ring-blue-500"
                aria-label="Close session player"
                data-testid="close-replay-modal-btn"
              >
                <FaTimes className="text-base" />
              </button>
            </div>

            {/* Modal Body / Player Display */}
            <div className="overflow-y-auto flex-1 p-4 sm:p-6 bg-slate-50/50">
              <SessionReplayPlayer
                session={sessionData}
                isLoading={isLoading}
                error={error}
                reloadNonce={refreshNonce}
                onReload={() => fetchSession(activeSessionId)}
              />
            </div>
          </div>
        </div>
      )}

      {/* Recent Recordings Discovery Table */}
      <div
        className="bg-white border border-slate-200 rounded-xl overflow-hidden shadow-sm flex flex-col"
        data-testid="replay-recordings-table-card"
      >
        <div className="flex items-center justify-between py-3.5 px-5 bg-slate-50 border-b border-slate-200">
          <h4 className="m-0 text-sm font-bold text-slate-900">Recent Session Recordings</h4>
          <button
            type="button"
            className="inline-flex items-center gap-1.5 px-2.5 py-1 text-xs font-medium text-slate-700 bg-slate-100 hover:bg-slate-200 border border-slate-200 rounded-md transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            onClick={() => {
              fetchRecordings();
              if (activeSessionId) {
                fetchSession(activeSessionId);
              }
            }}
            disabled={isLoadingRecordings || isLoading}
            title="Refresh recordings"
            data-testid="refresh-recordings-btn"
          >
            <FaSyncAlt className={isLoadingRecordings ? 'animate-spin' : ''} /> Refresh
          </button>
        </div>

        <div className="overflow-x-auto">
          {isLoadingRecordings ? (
            <div className="p-7 text-center text-slate-400 text-[13px]" data-testid="recordings-loading">
              Loading recent session recordings...
            </div>
          ) : recordings.length === 0 ? (
            <div className="p-7 text-center text-slate-400 text-[13px]" data-testid="recordings-empty">
              {recordingsError || 'No recent session recordings available for this site collection.'}
            </div>
          ) : (
            <table className="w-full border-collapse text-[13px] text-left" data-testid="replay-recordings-table">
              <thead>
                <tr className="bg-slate-50 border-b border-slate-200">
                  <th className="px-4 py-2.5 text-slate-500 font-semibold text-[11px] uppercase tracking-wider">Session Identifier</th>
                  <th className="px-4 py-2.5 text-slate-500 font-semibold text-[11px] uppercase tracking-wider">Status</th>
                  <th className="px-4 py-2.5 text-slate-500 font-semibold text-[11px] uppercase tracking-wider whitespace-nowrap">Created At</th>
                  <th className="px-4 py-2.5 text-slate-500 font-semibold text-[11px] uppercase tracking-wider whitespace-nowrap">Size</th>
                  <th className="px-4 py-2.5 text-slate-500 font-semibold text-[11px] uppercase tracking-wider whitespace-nowrap">Retention Date and Time</th>
                  <th className="px-4 py-2.5 text-slate-500 font-semibold text-[11px] uppercase tracking-wider">Segments</th>
                  <th className="px-4 py-2.5 text-slate-500 font-semibold text-[11px] uppercase tracking-wider">Action</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {recordings.map((rec) => {
                  const sId = rec.sessionId || rec.recordingId;
                  const statusVal = rec.state || rec.status || 'ready';
                  const isActive = activeSessionId === sId;
                  return (
                    <tr
                      key={sId}
                      className={`transition-colors ${isActive ? 'bg-blue-50/70' : 'hover:bg-slate-50/80'}`}
                      data-testid={`recording-row-${sId}`}
                    >
                      <td className="px-4 py-3 align-middle">
                        <code className="font-mono text-xs text-blue-600 bg-blue-50 px-1.5 py-0.5 rounded border border-blue-100">
                          {sId}
                        </code>
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <span className={`inline-flex items-center px-2 py-0.5 rounded-full text-[11px] font-semibold capitalize border ${getStatusBadgeClass(statusVal)}`}>
                          {statusVal}
                        </span>
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap text-slate-700">
                        {formatDateTime(rec.createdAtUtc || rec.startedAtUtc || rec.createdAt)}
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap text-slate-700">
                        {formatBytes(rec.totalBytes ?? rec.sizeBytes ?? rec.size)}
                      </td>
                      <td className="px-4 py-3 align-middle whitespace-nowrap text-slate-700">
                        {formatDateTime(rec.retentionExpiresAtUtc || rec.retentionExpiresAt || rec.retentionDate)}
                      </td>
                      <td className="px-4 py-3 align-middle text-slate-700">
                        {rec.segmentCount || rec.segments?.length || 1}
                      </td>
                      <td className="px-4 py-3 align-middle">
                        <button
                          type="button"
                          className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-semibold rounded-md transition-colors shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1"
                          onClick={() => handleSelectRecording(sId)}
                          data-testid={`play-recording-btn-${sId}`}
                        >
                          <FaPlayCircle /> Inspect
                        </button>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}

SessionReplayTab.propTypes = {
  organizationId: PropTypes.string,
  siteCollectionId: PropTypes.string,
  fromDate: PropTypes.string,
  toDate: PropTypes.string,
  initialSessionId: PropTypes.string,
  hasSessionReplayFeature: PropTypes.bool,
  refreshNonce: PropTypes.number,
  onLoadingChange: PropTypes.func
};
